/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { beDateFormat, classNames, usePrevious, validateKeyGen, superUniqueClassName, useWindowDimensions, LG, Button } from '@fl/cmsch-fe-library';
import { debounce, replace } from 'lodash/fp';
import moment, { Moment } from 'moment';
import React, { FC, memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Opt } from 'ts-opt';
import { earTagRegexGen } from 'api/gen/EarTag';
import { milkedTotalRangeMaximum, milkedTotalRangeMinimum } from 'api/gen/MilkedTotalRange';
import { technicianNumberRegexGen } from 'api/gen/TechnicianNumber';
import { doubleGutter, gutter } from 'app/consts';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { AsyncValidationSource } from 'utils/async-validation-source';
import { countDecimalPlaces } from 'utils/count-decimal-places';
import { formHelpers, useForm } from 'utils/forms';
import { insertedDateOnKeyboard } from 'utils/inserted-date-on-keyboard';
import { initialMilkabilityFormValues, milkabilityReportFormName, MilkabilityFormValues } from './form-values';
import { validate } from './validate';
import { isDateWithinTwoControlYears } from '../../utils/is-date-within-two-control-years';
import styles from './styles.sass';

/**
 * <h3>Milkability report form</h3>
 *
 * <ul>
 *  <li>When there is a synchronously valid value entered into a field, the next field gets focused.</li>
 *  <li>On change of any field, the <i>apmv</i> value is reset and async validation runs when
 *      synchronous validation passes.</li>
 *  <li>When editing an existing milkability report, the ear tag input is disabled.</li>
 *  <li>When a new report is submitted, it is added to the created reports table.</li>
 *  <li>When a report is successfully created/updated, the form is reset to default state.</li>
 *  <li>When creating a new milkability report, the initial focus should be on technician number.</li>
 * </ul>
 */

const waitTime = 1000;
const earTagLastCharIndex = 13;
interface Props {
  /** Is async validation in progress */
  validatingMilkabilityReportForm?: boolean;
  /** Whether the form is currently editing an existing report. */
  editingMilkabilityReport: boolean;
  /** Ear-tag input ref used for focus orchestration. */
  earTagInputRef: RefObject<HTMLInputElement>;
  /** Technician number input ref used for initial focus and resets. */
  technicianNumberInputRef: RefObject<HTMLInputElement>;
  /** Runs backend validation for the current form values. */
  validateMilkabilityData(source: AsyncValidationSource): void;
  /** Submits the normalized form values. */
  onSubmit(data: MilkabilityFormValues): void;
  /** Gets called on change of any field */
  calculateApmvAndValidateReport(source: AsyncValidationSource): void;
  /** Resets form to initial state */
  cancelEditMilkabilityReport(): void;
}
const isMilkedTotalValid = (value: number): boolean => milkedTotalRangeMinimum <= value && value <= milkedTotalRangeMaximum;
const isDateValidForReport = (date: Moment): boolean => {
  const today = moment();
  return date.isValid() && isDateWithinTwoControlYears(date, today) && today.isAfter(date);
};
const validateKey = validateKeyGen<MilkabilityFormValues>();
const numberWithOneDecimalRegex = /^\d{0,2}[,.]\d$/;
const MilkabilityReportFormBase: FC<Props> = props => {
  const {
    validatingMilkabilityReportForm,
    editingMilkabilityReport,
    earTagInputRef,
    technicianNumberInputRef,
    validateMilkabilityData,
    calculateApmvAndValidateReport,
    cancelEditMilkabilityReport,
    onSubmit
  } = props;
  const examinationDateRef = useRef<HTMLInputElement>(null);
  const milkedTotalRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const {
    t,
    tCommon
  } = useOurTranslation('milkability');
  const pendingManualDateFocusRef = useRef(false);
  const lastTypedDateValueRef = useRef<string | null>(null);
  const earTag = useSelector(formHelpers.formValues(milkabilityReportFormName)).prop('earTag').orNull();
  const earTagPreviousValue = usePrevious(earTag);
  const [savedEarTag, setSavedEarTag] = useState(earTagPreviousValue);
  const {
    width
  } = useWindowDimensions();
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [disabledHover, setDisabledHover] = useState(false);
  const {
    Form,
    Fields,
    change,
    blur,
    touch,
    submitting,
    initialized
  } = useForm({
    form: milkabilityReportFormName,
    initialValues: initialMilkabilityFormValues,
    onSubmit,
    validate,
    destroyOnUnmount: true
  });
  const previousSubmitting = usePrevious(submitting);
  useEffect(() => {
    if (earTagPreviousValue && submitting !== previousSubmitting) {
      setSavedEarTag(earTagPreviousValue);
    }
  }, [earTagPreviousValue, previousSubmitting, submitting]);
  useEffect(() => {
    if (earTagPreviousValue) {
      setSavedEarTag(earTagPreviousValue);
    }
    return (): void => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (savedEarTag && submitting !== previousSubmitting) {
      change('earTag', savedEarTag);
    }
  }, [change, previousSubmitting, savedEarTag, submitting]);
  useEffect(() => {
    if (initialized) {
      technicianNumberInputRef.current?.focus();
    }
  }, [editingMilkabilityReport, initialized, technicianNumberInputRef]);
  useEffect(() => {
    if (!disabledHover) return;
    const handleRestoreHover = (): void => {
      setDisabledHover(false);
    };
    document.addEventListener('mousemove', handleRestoreHover);
    document.addEventListener('keydown', handleRestoreHover);
    return (): void => {
      document.removeEventListener('mousemove', handleRestoreHover);
      document.removeEventListener('keydown', handleRestoreHover);
    };
  }, [disabledHover]);
  const validateMilkabilityDataDebounced = useMemo(() => debounce(waitTime, validateMilkabilityData), [validateMilkabilityData]);
  const calculateApmvAndValidateReportDebounced = useMemo(() => debounce(waitTime, calculateApmvAndValidateReport), [calculateApmvAndValidateReport]);
  const onChange = useCallback((source: AsyncValidationSource) => {
    change('apmv', null);
    calculateApmvAndValidateReportDebounced(source);
  }, [calculateApmvAndValidateReportDebounced, change]);
  const handleCancel = useCallback(() => {
    cancelEditMilkabilityReport();
  }, [cancelEditMilkabilityReport]);
  const focusAfterDate = useCallback(() => {
    setTimeout(() => {
      pendingManualDateFocusRef.current = false;
      (editingMilkabilityReport ? milkedTotalRef : earTagInputRef).current?.focus();
    });
  }, [editingMilkabilityReport, earTagInputRef]);
  const onDateChange = useCallback((value: Opt<string>) => {
    onChange('examinationDate');
    value.onSome(date => {
      const momentDate = moment(date, beDateFormat);
      if (isDateValidForReport(momentDate)) {
        /* HACK for issue #11076 http://redmine.favorlogic.com/issues/11076 ant is adding ant-picker-focused
         * and we need to remove it
         */
        const [element] = document.getElementsByClassName(superUniqueClassName);
        element?.classList.remove('ant-picker-focused');
        if (width < LG) {
          setDisabledHover(true);
        }
        examinationDateRef.current?.blur();
        blur('examinationDate', date);
        if (!pendingManualDateFocusRef.current) {
          focusAfterDate();
        }
      }
    });
  }, [onChange, blur, focusAfterDate, width]);
  const onTechnicianNumberChange = useCallback((value: Opt<string>) => {
    validateMilkabilityDataDebounced('technicianNumber');
    if (value.map(val => val.match(technicianNumberRegexGen())).orFalse()) {
      setTimeout(() => examinationDateRef.current?.focus());
    }
  }, [validateMilkabilityDataDebounced]);
  const onMilkedTotalChange = useCallback(() => onChange('milkedTotal'), [onChange]);
  const onMilkingTimeChange = useCallback(() => onChange('milkingTime'), [onChange]);

  // If last character of ear tag was inputted, focus next field
  const onEarTagChange = useCallback((value: Opt<string>) => {
    onChange('earTag');
    value.onSome(val => {
      if (earTag?.indexOf('_') === earTagLastCharIndex && !val.includes('_')) {
        setTimeout(() => milkedTotalRef.current?.focus());
      }
    });
  }, [earTag, onChange]);
  const onEarTagPaste = useCallback((value: Opt<string>) => {
    onChange('earTag');
    if (value.orElse('').match(earTagRegexGen())) {
      if (timeoutId) clearTimeout(timeoutId);
      setTimeoutId(setTimeout(() => milkedTotalRef.current?.focus()));
    }
  }, [onChange, timeoutId]);

  // If inputted value matches number with one decimal pattern and is valid, focus next field
  const handleMilkedTotalInput = useCallback((text: string) => {
    if (text.match(numberWithOneDecimalRegex)) {
      const numberValue = parseFloat(replace(',', '.')(text));
      if (!isNaN(numberValue) && isMilkedTotalValid(numberValue)) {
        // order is important
        timeInputRef.current?.focus();
        change('milkedTotal', numberValue);
      } else {
        touch('milkedTotal');
      }
    } else {
      if (countDecimalPlaces(text) > 1) {
        timeInputRef.current?.focus();
      }
    }
  }, [change, touch]);
  const handleDatePickerKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Change tab behavior from focusing date picker dropdown to focusing form fields
    const previousDate = e.currentTarget.value;
    const position = e.currentTarget.selectionStart;
    const {
      key,
      shiftKey
    } = e;
    if (key === 'Tab' && !editingMilkabilityReport) {
      e.preventDefault();
      if (shiftKey) {
        technicianNumberInputRef.current?.focus();
      } else {
        earTagInputRef.current?.focus();
      }
    }
    const {
      date,
      formattedDate,
      isValid,
      isEmpty
    } = insertedDateOnKeyboard(previousDate, key, position);
    // Every time, when is Date in valid format, set value of examinationDate, cause of showing errors message
    if (isValid) {
      change('examinationDate', formattedDate);
      if (isDateValidForReport(date) && lastTypedDateValueRef.current !== formattedDate) {
        pendingManualDateFocusRef.current = true;
        // If a valid date is inputted, focus next, non-disabled field
        focusAfterDate();
      }
      lastTypedDateValueRef.current = formattedDate;
    } else if (isEmpty) {
      change('examinationDate', null);
      lastTypedDateValueRef.current = null;
      pendingManualDateFocusRef.current = false;
    }
  }, [change, earTagInputRef, editingMilkabilityReport, focusAfterDate, technicianNumberInputRef]);
  const classes = classNames(styles.reportForm, disabledHover && styles.disabledHover);
  return <Form data-sentry-element="Form" data-sentry-component="MilkabilityReportFormBase" data-sentry-source-file="index.tsx">
            <Ant.Row gutter={[doubleGutter, gutter]} justify="end" className={classes} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.Input type="number" name={validateKey('technicianNumber')} label={t('technicianNumber')} onFieldChange={onTechnicianNumberChange} inputRef={technicianNumberInputRef} isRequired autoFocus={initialized && editingMilkabilityReport} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.DateInput name={validateKey('examinationDate')} label={t('examinationDate')} onFieldChange={onDateChange} dontIncludeFuture inputRef={examinationDateRef} onKeyDown={handleDatePickerKeyDown} isRequired clearable data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.EarTagInput name={validateKey('earTag')} label={tCommon('earTagTooltip')} placeholder={tCommon('earTagPlaceholder')} onFieldChange={onEarTagChange} disabled={editingMilkabilityReport} inputRef={earTagInputRef} isRequired onPasting={onEarTagPaste} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.NumberInput name={validateKey('milkedTotal')} label={t('milkedTotal')} onFieldChange={onMilkedTotalChange} inputRef={milkedTotalRef} fullWidth isRequired disableArrowControls onFieldInput={handleMilkedTotalInput} precision={1} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.TimeInput name={validateKey('milkingTime')} label={t('milkingTime')} isRequired onFieldChange={onMilkingTimeChange} inputRef={timeInputRef} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} md={12} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Fields.NumberInput name={validateKey('apmv')} label={t('apmv')} fullWidth disabled precision={2} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </Ant.Col>
                <Ant.Col xs={24} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <label className="invisible">.</label>
                    <Button type="button" loading={submitting || validatingMilkabilityReportForm} onClick={handleCancel} block data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {tCommon('cancel')}
                    </Button>
                </Ant.Col>
                <Ant.Col xs={24} lg={8} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <label className="invisible">.</label>
                    <Button type="submit" visuals="primary" key="submit" loading={submitting || validatingMilkabilityReportForm} block testId="submit-button" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {tCommon(validatingMilkabilityReportForm ? 'validating' : 'save')}
                    </Button>
                </Ant.Col>
            </Ant.Row>
        </Form>;
};

/**
 * Renders the milkability form with focus flow, async validation, and APMV recalculation.
 *
 * @param props Form refs, flags, and callbacks used by the report workflow.
 * @returns The milkability report form.
 *
 * @example
 * <MilkabilityReportForm
 *   validatingMilkabilityReportForm={false}
 *   editingMilkabilityReport={false}
 *   earTagInputRef={earTagInputRef}
 *   technicianNumberInputRef={technicianNumberInputRef}
 *   validateMilkabilityData={validateMilkabilityData}
 *   onSubmit={handleSubmit}
 *   calculateApmvAndValidateReport={calculateApmvAndValidateReport}
 *   cancelEditMilkabilityReport={cancelEditMilkabilityReport}
 * />
 */
export const MilkabilityReportForm = memo(MilkabilityReportFormBase);